import { Injectable, computed, signal } from '@angular/core';
import { Permission, ROLES, RoleKey } from '../models/role.model';

export type AccountType = 'solo' | 'firm';

export interface AccountState {
  type: AccountType;
  /** The signed-in person's name. */
  userName: string;
  /** Firm/practice name. For solo accounts this is just the person's name. */
  firmName: string;
  email: string;
  /** This member's role in the firm, from /firm/me. Decides what the UI offers. */
  role: RoleKey;
}

const KEY = 'acc_account';

/**
 * Holds the signed-in account: whether it's a solo accountant or a firm, plus the display name.
 *
 * "Firm of one": a solo account is internally still a firm with a single owner — so upgrading to a
 * team later needs no migration. The `type` flag only decides what UI shows (Team/Roles are hidden
 * for solo). Placeholder persistence via sessionStorage until real auth is wired.
 */
@Injectable({ providedIn: 'root' })
export class AccountService {
  private readonly state = signal<AccountState>(this.load());

  readonly account = this.state.asReadonly();
  readonly isFirm = computed(() => this.state().type === 'firm');
  readonly userName = computed(() => this.state().userName);
  readonly firmName = computed(() => this.state().firmName);
  readonly email = computed(() => this.state().email);
  readonly initials = computed(() => {
    const parts = this.state().userName.trim().split(/\s+/);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || 'AC';
  });
  readonly role = computed<RoleKey>(() => this.state().role);
  /** Header role label — the member's actual role; solo has no team, so it reads "Individual". */
  readonly roleLabel = computed(() =>
    this.isFirm() ? (ROLES[this.role()]?.label ?? 'Member') : 'Individual',
  );

  /**
   * Whether this member's role grants a permission. The backend enforces the same rules — this only
   * decides what the UI offers, so a member is never shown controls that would 403.
   */
  can(permission: Permission): boolean {
    return ROLES[this.role()]?.permissions.includes(permission) ?? false;
  }

  set(state: AccountState): void {
    this.state.set(state);
    try {
      sessionStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      /* ignore */
    }
  }

  clear(): void {
    try {
      sessionStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
  }

  private load(): AccountState {
    try {
      const raw = sessionStorage.getItem(KEY);
      if (raw) return JSON.parse(raw) as AccountState;
    } catch {
      /* ignore */
    }
    // Neutral default until /firm/me populates the real account on login. The role defaults to the
    // least-privileged one so a missing/failed /firm/me can never hand out owner-level UI.
    return { type: 'firm', userName: '', firmName: '', email: '', role: 'viewer' };
  }
}
